import { roleHelpers } from '@/services/utils';
import { getPinia } from '@/createPiniaSingleton';
import { useReportFiltersStore } from '@/stores/reports/useReportFiltersStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { REPORT_FILTER_DEBOUNCE_MS } from '@/constants/reportFilterConstants';
import { stateToLegacyFilter } from '@/utils/reportQueryHelpers';
import ReportFilterPanel from '../components/ReportFilterPanel.vue';
import { mountReportVue, withReportFilterPanelFeature } from './reportFilterPanelHost';
import { createRoutePeriodTracker, loadPanelContext, onFiltersChange, standardDescriptor } from './reportFilterPanelSync';

/**
 * Standard reports' filter sidebar. With the `reportFilterPanel` feature it hosts
 * `ReportFilterPanel` and rebroadcasts store changes in the legacy payload shape;
 * without it, the legacy template runs unchanged. Delete `linkLegacy` and
 * `views/commonFilter.html` once the feature is on for every firm (sc-48496).
 */
angular.module('leanlaw').directive('commonFilter', ['$rootScope', '$injector', '$compile', 'filterService', 'userService', 'leanLawAuth', 'leanLawApi', 'messageBus', 'lists', 'matterTagsService', function ($rootScope, $injector, $compile, filterService, userService, leanLawAuth, leanLawApi, messageBus, lists, matterTagsService) {
  // Per-session on purpose — see createRoutePeriodTracker in reportFilterPanelSync.ts.
  let consumedRoutePeriodId = null;
  const applyRoutePeriod = createRoutePeriodTracker();
  function linkLegacy($scope) {
    const {
      handleError
    } = useSnackbarHandler();
    // "Show Matter Involving" is shared with the generic engine, so it lives in the
    // store rather than in a singleton this directive owns.
    const reportFilters = useReportFiltersStore(getPinia());
    userService.me().then(me => {
      $scope.me = me;
      matterTagsService.getMatterTags().then(mattertags => {
        $scope.matterTags = mattertags;
      });
      userService.list({
        status: 'all'
      }).then(users => {
        const lockByDefault = me.role === 'lead' || me.role === 'timekeeper' || me.role === 'paralegal';
        if (me.permissions.matters.autoAssign || !lockByDefault) {
          $scope.accounts = users;
        } else {
          $scope.lockAccountSelection = true;
          $scope.accounts = [me];
        }
        $scope.accounts = filterService.formatDisabledUser($scope.accounts);
        // Retrieve the globally saved "Show Matter Involving" account filter state
        const savedGlobalReportsScopeAccount = reportFilters.filters.scopeAccount;
        const isScopeAccountChanged = reportFilters.scopeAccountTouched;
        // Check if the global filter has been previously set by user
        if (isScopeAccountChanged) {
          if (savedGlobalReportsScopeAccount) {
            // Find the matching account in the current user's available accounts list
            const matchingAccount = $scope.accounts.find(a => a.id === savedGlobalReportsScopeAccount.id);
            if (matchingAccount) {
              // Apply the saved account filter to the current view
              $scope.filter.filter.scopeAccount = matchingAccount;
            }
          } else {
            // If the global filter was explicitly set to null, apply that preference
            $scope.filter.filter.scopeAccount = null;
          }
        } else {
          // If no global filter has been set yet, apply default behavior based on user role:
          // - Operators and accountants no autopopulate the filter
          // - All other roles (attorneys, paralegals, etc.) populate the filter with the current user

          if (!roleHelpers.isOperatorOrAccountantRole($scope.me.role)) {
            $scope.filter.filter.scopeAccount = $scope.me; // Default to current user for most roles
          } else {
            $scope.filter.filter.scopeAccount = null; // Default to no filter for operators/accountants
          }
        }
      });
      $scope.showTimekeeper = me.permissions.time.createOthers;
    }, e => handleError(e));
    $scope.scopeChange = function (prop) {};
    $scope.archiveOptions = [{
      label: 'Exclude Archived',
      status: 'active'
    }, {
      label: 'Include Archived',
      status: 'all'
    }, {
      label: 'Only Archived',
      status: 'archived'
    }];
    $scope.distributionStatusOptions = lists.distributionStatuses;
    $scope.billedOptions = lists.billedOptions;
    leanLawApi.listPaymentDistributions().then(list => {
      $scope.distributions = list.distributions.sort((a, b) => new Date(b.date) - new Date(a.date));
    });
    $scope.byDistribution = distributionFeatures => function (distribution) {
      if (!distributionFeatures) {
        return distribution.status !== 'Draft';
      }
      return true;
    };
    $scope.periods = filterService.periods(false);

    // This directive is rebuilt per report now, so the selections are restored from
    // the store rather than from its own lifetime. Always re-resolved against the
    // freshly built option lists: these selects have no `track by`, so ng-options
    // needs the identical object, and sendFilterUpdate flattens the period in place.
    const persisted = reportFilters.filters;
    const restore = (options, key, value) => value === null || value === undefined ? undefined : options.find(o => o[key] === value);
    const optionId = option => option && typeof option === 'object' ? option.id : option;

    // Only half the periods carry an id — This Week, Last Week, Today, Custom Period
    // and "<month> and earlier" are label-only, so the label is the key for those.
    const periodKey = period => (period ? period.id || period.label : null) || null;

    // sendFilterUpdate flattens the selected period, so a window may be nested under
    // `range` or sitting directly on the object.
    const spanOf = period => period && period.range || period || {};
    const sameInstant = (a, b) => a == null && b == null || Boolean(a && b && +new Date(a) === +new Date(b));
    const sameSpan = (a, b) => sameInstant(spanOf(a).startDate, spanOf(b).startDate) && sameInstant(spanOf(a).endDate, spanOf(b).endDate);

    /**
     * Match a period across two lists that are built by different modules — this
     * directive uses filterService.periods(false), the store uses reportPeriods(true),
     * which formats the month labels differently. Falls back to the window itself,
     * which both compute identically.
     */
    const matchPeriod = (options, period) => {
      if (!period) return undefined;
      const found = period.id && options.find(o => o.id === period.id) || options.find(o => o.label === period.label) || options.find(o => sameSpan(o, period));
      // A custom period carries the user's own window, which no canonical option
      // matches. Returning nothing there is what stops the query silently snapping
      // back to the canonical option's default dates.
      return found && sameSpan(found, period) ? found : undefined;
    };
    const matchOption = (options, value) => {
      const id = optionId(value);
      return id === null || id === undefined ? undefined : options.find(o => o.id === id);
    };
    const restorePeriod = value => {
      const key = periodKey(value);
      const match = key && $scope.periods.find(p => periodKey(p) === key) || matchPeriod($scope.periods, value);
      // A custom period's dates are the user's own. Every other period recomputes its
      // range from the label, which is what we want when the day has moved on.
      if (match && match.custom && value.startDate && value.endDate) {
        match.range = {
          startDate: value.startDate,
          endDate: value.endDate
        };
      }
      return match || undefined;
    };

    // Honour a period handed in by a dashboard card once, then let the stored
    // selection win — see consumedRoutePeriodId above.
    const routePeriod = $scope.periods.find(p => p.label === $scope.range) || $scope.periods.find(p => p.id === $scope.id);
    const freshRoutePeriod = routePeriod && periodKey(routePeriod) !== consumedRoutePeriodId ? routePeriod : null;
    if (freshRoutePeriod) consumedRoutePeriodId = periodKey(freshRoutePeriod);

    // Default filter
    $scope.filter = {
      // hack: we have two billed filters because two different report sections need different defaults for that filter
      billedFilter: restore($scope.billedOptions, 'id', optionId(persisted.billed)) || $scope.billedOptions.find(b => b.id === 'all'),
      contingencyBilledFilter: restore($scope.billedOptions, 'id', optionId(persisted.contingencyBilled)) || $scope.billedOptions.find(b => b.id === 'WIP'),
      // Archive options key on `status`, not `id` — legacyFilterToState stores the
      // bare id for the same reason.
      archived: restore($scope.archiveOptions, 'status', optionId(persisted.status)) || $scope.archiveOptions[1],
      filter: {
        range: freshRoutePeriod || restorePeriod(persisted.dateRange) || $scope.periods[0],
        scopeAccount: reportFilters.filters.scopeAccount || undefined
      },
      matterTag: {
        id: undefined
      },
      show: {
        billed: true,
        collected: true
      }
    };
    $scope.distributionChange = function () {
      const {
        distributionId,
        distributionStatus
      } = $scope.filter;
      $scope.filter.show.billed = !$scope.revenueReport || !distributionId && !distributionStatus;
    };

    /**
     * Store the store's own option objects, not this directive's. The generic engine
     * binds the same state with ng-options and no `track by` (reportFilter.html), so
     * an equal-but-different object leaves its dropdown blank while the query quietly
     * keeps working. Falls back to the local object, which is no worse than before.
     *
     * Narrow on purpose: adoptLegacyFilter would also write showEntries/showCharges
     * defaults this directive has no opinion on, over the generic engine's values.
     * matterTag is absent because commonFilter.html binds it to a phantom `$scope.`
     * path, so a restored tag would filter the report without showing in the select.
     */
    function persistFilters() {
      const range = $scope.filter.filter.range;
      const archived = $scope.filter.archived;
      reportFilters.setFilters({
        dateRange: matchPeriod(reportFilters.periodOptions, range) || range,
        billed: matchOption(reportFilters.billedOptions, $scope.filter.billedFilter) || $scope.filter.billedFilter,
        contingencyBilled: matchOption(reportFilters.billedOptions, $scope.filter.contingencyBilledFilter) || $scope.filter.contingencyBilledFilter,
        // Archive options key on `status` here and on `id` in the store, and the
        // store's consumers bind the option object, not the bare id.
        status: matchOption(reportFilters.archivedOptions, archived && archived.status) || archived && archived.status
      });
    }
    function sendFilterUpdate(newValue) {
      const filter = angular.copy(newValue || $scope.filter);
      $scope.filter.filter.scope = scopeFromFilter(filter);
      $scope.filter.range = Object.assign($scope.filter.filter.range, $scope.filter.filter.range.range);
      delete $scope.filter.filter.range.range;
      persistFilters();
      $rootScope.$broadcast('commonFilterService-statechanged', filter);
    }
    function sendScopeAccountUpdate(newValue) {
      const filter = angular.copy(newValue || $scope.filter);
      if (filter && filter.filter) {
        reportFilters.setScopeAccount(filter.filter.scopeAccount === undefined ? null : filter.filter.scopeAccount);
      }
      $rootScope.$broadcast('commonFilterService-statechanged', filter);
    }
    function scopeFromFilter(filter) {
      return filterService.scopeFromFilter(filter.filter);
    }
    $scope.changeMatterTag = function (selectedMatterTag) {
      if (selectedMatterTag) {
        $scope.filter.matterTag.id = selectedMatterTag.id;
      } else {
        $scope.filter.matterTag.id = null; // or undefined
      }
    };

    // Debounced filter updates to prevent page flashing during rapid changes
    let filterUpdateTimeout;
    let scopeAccountTimeout;
    $scope.$watch('filter', (newValue, oldValue) => {
      if (!oldValue) return;
      // Ignore initial change event:
      if (newValue === oldValue) return;

      // Clear any pending filter update
      if (filterUpdateTimeout) {
        clearTimeout(filterUpdateTimeout);
      }

      // Debounce filter changes by 300ms to prevent flashing during rapid changes
      filterUpdateTimeout = setTimeout(() => {
        sendFilterUpdate(newValue);
        filterUpdateTimeout = null;
      }, 300);
    }, true);

    // Specific watch for scopeAccount changes (also debounced)
    $scope.$watch('filter.filter.scopeAccount', (newValue, oldValue) => {
      if (newValue === oldValue) return;

      // Clear any pending scope account update
      if (scopeAccountTimeout) {
        clearTimeout(scopeAccountTimeout);
      }

      // Debounce scope account changes by 300ms
      scopeAccountTimeout = setTimeout(() => {
        sendScopeAccountUpdate($scope.filter);
        scopeAccountTimeout = null;
      }, 300);
    });
    const onFilterDependencyReady = () => {
      sendFilterUpdate();
    };

    // Clean up timeouts and the message bus listener on scope destroy
    $scope.$on('$destroy', () => {
      if (filterUpdateTimeout) {
        clearTimeout(filterUpdateTimeout);
        filterUpdateTimeout = null;
      }
      if (scopeAccountTimeout) {
        clearTimeout(scopeAccountTimeout);
        scopeAccountTimeout = null;
      }
      // A stale listener doesn't just leak: addMessage drops a queued message once
      // any listener exists, so dead ones swallow the replay a fresh filter needs.
      messageBus.removeListener(onFilterDependencyReady);
    });
    messageBus.addListener('filterDependency-ready', onFilterDependencyReady);
  }
  function linkVue($scope, element, attrs) {
    const store = useReportFiltersStore(getPinia());
    const {
      handleError
    } = useSnackbarHandler();
    let alive = true;

    // Isolate bindings only sync on the next digest, but a new report asks for its
    // filters during the route change — read its flags straight from the shell.
    const read = name => attrs[name] ? $scope.$parent.$eval(attrs[name]) : undefined;
    const reportFlags = () => ({
      hideDate: read('hideDate'),
      hideClientMatter: read('hideClientMatter'),
      hideMatterTags: read('hideMatterTags'),
      showBilled: read('showBilled'),
      showContingencyBilled: read('showContingencyBilled'),
      revenueReport: read('revenueReport'),
      hideExpense: read('hideExpense')
    });
    store.configurePanel({
      family: 'standard',
      show: standardDescriptor(reportFlags()),
      accounts: [],
      lockAccountSelection: false,
      distributions: [],
      trustAccounts: []
    });
    $scope.$watch(() => standardDescriptor(reportFlags()), show => store.configurePanel({
      show
    }), true);
    applyRoutePeriod(store, read('range'), read('id'));
    loadPanelContext(store, 'standard', {
      me: () => userService.me(),
      users: () => userService.list({
        status: 'all'
      }),
      distributions: () => leanLawApi.listPaymentDistributions(),
      formatDisabledUser: filterService.formatDisabledUser,
      isAlive: () => alive,
      onError: handleError
    });
    const broadcast = () => {
      const flags = reportFlags();
      $rootScope.$broadcast('commonFilterService-statechanged', stateToLegacyFilter(store.visibleFilters(standardDescriptor(flags)), Boolean(flags.revenueReport)));
    };

    // Debounced so rapid changes don't make the report flash.
    const stopWatching = onFiltersChange(store, () => $rootScope.$applyAsync(broadcast), REPORT_FILTER_DEBOUNCE_MS);

    // Each report load: catch period windows up with today before answering it.
    const onReportReady = () => {
      store.refreshPeriods();
      broadcast();
    };
    $scope.$on('$destroy', () => {
      alive = false;
      stopWatching();
      // A stale listener doesn't just leak: addMessage drops a queued message once
      // any listener exists, so dead ones swallow the replay a fresh filter needs.
      messageBus.removeListener(onReportReady);
    });
    messageBus.addListener('filterDependency-ready', onReportReady);
    mountReportVue(ReportFilterPanel, $scope, element, $injector);
  }
  return {
    restrict: 'E',
    scope: {
      filterModel: '=',
      showContingencyBilled: '=',
      revenueReport: '=',
      hideExpense: '=',
      showBilled: '=',
      hideClientMatter: '=',
      hideMatterTags: '=',
      hideDate: '=',
      range: '=',
      id: '='
    },
    link($scope, element, attrs) {
      let alive = true;
      $scope.$on('$destroy', () => {
        alive = false;
      });
      withReportFilterPanelFeature(userService, leanLawAuth.features, () => alive, enabled => {
        if (enabled) {
          linkVue($scope, element, attrs);
          return;
        }
        element.html(require('./views/commonFilter.html'));
        $compile(element.contents())($scope);
        linkLegacy($scope);
        $scope.$applyAsync();
      });
    }
  };
}]);